import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiFolder, FiFile, FiCheck, FiSquare, FiCheckSquare, FiDownload, FiRefreshCw } = FiIcons;

function FileExplorer({ files, selectedFiles, onFileSelection, onGenerateText, onReset, isProcessing }) {
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  const buildFileTree = () => {
    const tree = {};
    
    files.forEach(file => {
      const parts = file.path.split('/');
      let current = tree;
      
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        
        if (i === parts.length - 1) {
          current[part] = file;
        } else {
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
      }
    });
    
    return tree;
  };

  const toggleFolder = (folderPath) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };

  const toggleAllInFolder = (node, basePath = '') => {
    const filePaths = [];
    
    const collectFiles = (obj, path) => {
      Object.entries(obj).forEach(([key, value]) => {
        const currentPath = path ? `${path}/${key}` : key;
        if (value.content !== undefined) {
          filePaths.push(value.path);
        } else {
          collectFiles(value, currentPath);
        }
      });
    };
    
    collectFiles(node, basePath);
    
    const allSelected = filePaths.every(path => selectedFiles.has(path));
    
    filePaths.forEach(path => {
      onFileSelection(path, !allSelected);
    });
  };

  const renderTree = (node, basePath = '', level = 0) => {
    return Object.entries(node).map(([key, value]) => {
      const currentPath = basePath ? `${basePath}/${key}` : key;
      const isFile = value.content !== undefined;
      
      if (isFile) {
        const isSelected = selectedFiles.has(value.path);
        
        return (
          <motion.div
            key={value.path}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className={`
              flex items-center space-x-2 py-2 px-3 rounded-lg cursor-pointer transition-colors
              ${isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50'}
            `}
            style={{ marginLeft: `${level * 20}px` }}
            onClick={() => onFileSelection(value.path, !isSelected)}
          >
            <SafeIcon 
              icon={isSelected ? FiCheckSquare : FiSquare} 
              className={`text-lg ${isSelected ? 'text-blue-600' : 'text-slate-400'}`} 
            />
            <SafeIcon icon={FiFile} className="text-slate-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{key}</p>
              <p className="text-xs text-slate-500">{value.type} • {value.size} bytes</p>
            </div>
          </motion.div>
        );
      } else {
        const isExpanded = expandedFolders.has(currentPath);
        const folderFiles = [];
        
        const collectFolderFiles = (obj) => {
          Object.values(obj).forEach(val => {
            if (val.content !== undefined) {
              folderFiles.push(val.path);
            } else {
              collectFolderFiles(val);
            }
          });
        };
        
        collectFolderFiles(value);
        const allSelected = folderFiles.every(path => selectedFiles.has(path));
        
        return (
          <div key={currentPath}>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-2 py-2 px-3 rounded-lg hover:bg-slate-50 cursor-pointer"
              style={{ marginLeft: `${level * 20}px` }}
            >
              <SafeIcon 
                icon={allSelected ? FiCheckSquare : FiSquare} 
                className={`text-lg ${allSelected ? 'text-blue-600' : 'text-slate-400'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleAllInFolder(value, currentPath);
                }}
              />
              <SafeIcon 
                icon={FiFolder} 
                className="text-blue-500" 
                onClick={() => toggleFolder(currentPath)}
              />
              <span 
                className="flex-1 text-sm font-medium text-slate-700 cursor-pointer"
                onClick={() => toggleFolder(currentPath)}
              >
                {key} ({folderFiles.length})
              </span>
            </motion.div>
            
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                {renderTree(value, currentPath, level + 1)}
              </motion.div>
            )}
          </div>
        );
      }
    });
  };

  const fileTree = buildFileTree();
  const selectedCount = selectedFiles.size;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-800">
            File Explorer ({files.length} files)
          </h3>
          <button
            onClick={onReset}
            className="text-slate-500 hover:text-slate-700 transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} className="text-lg" />
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            {selectedCount} of {files.length} files selected
          </p>
          
          <motion.button
            onClick={onGenerateText}
            disabled={selectedCount === 0 || isProcessing}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiDownload} className="inline mr-2" />
            Generate Text
          </motion.button>
        </div>
      </div>
      
      <div className="p-4 max-h-96 overflow-y-auto">
        <div className="space-y-1">
          {renderTree(fileTree)}
        </div>
      </div>
    </div>
  );
}

export default FileExplorer;